import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";
import { DatePicker } from "@mui/x-date-pickers";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Brush,
} from "recharts";
import MilkingFormModal from "./widgets/MilkingFormModal";
import MilkingViewModal from "./widgets/MilkingViewModal";
import {
  createMilking,
  getAnimalMilkingSummary,
  getMilkTrends,
  updateMilking,
} from "../../api/milking";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { exportMilkingsCsv } from "../../api/milking";
import { getMilkingsByAnimalIdPaginated } from "../../api/milking";
import { useNavigate } from "react-router-dom";

const COLORS = {
  morning: "#3B82F6", // blue
  evening: "#F59E0B", // amber
  total: "#10B981", // emerald
  grid: "#E5E7EB", // neutral grid
};

function MilkingRecordCard({ row, onView, onEdit }) {
  const dateLabel = row?.date
    ? format(new Date(row.date), "MMM dd, yyyy")
    : "—";
  return (
    <MobileListCard
      title={dateLabel}
      subtitle={`Total: ${row.total_production ?? "—"} L`}
      fields={[
        { label: "Morning", value: `${row.morning_production ?? "—"} L` },
        { label: "Evening", value: `${row.evening_production ?? "—"} L` },
      ]}
      actions={
        <>
          <IconButton size="small" onClick={() => onView(row)} color="primary">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onEdit(row)} color="secondary">
            <EditIcon fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
}

const AnimalMilking = ({ animalId }) => {
  const isMobile = useIsMobileList("md");
  const navigate = useNavigate();
  const [milkings, setMilkings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trendPoints, setTrendPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("all");
  const [startDate, setStartDate] = useState(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [page, setPage] = useState(0); // zero-based for MUI
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [chartType, setChartType] = useState("bar"); // 'bar' | 'line'
  const [aggregation, setAggregation] = useState("daily"); // 'daily' | 'weekly' | 'monthly'

  const buildParams = useCallback(() => {
    const params = {};
    if (timeRange === "custom") {
      params.start_date = format(startDate, "yyyy-MM-dd");
      params.end_date = format(endDate, "yyyy-MM-dd");
    } else if (timeRange === "lastMonth") {
      params.start_date = format(
        startOfMonth(subMonths(new Date(), 1)),
        "yyyy-MM-dd"
      );
      params.end_date = format(
        endOfMonth(subMonths(new Date(), 1)),
        "yyyy-MM-dd"
      );
    } else if (timeRange === "thisMonth") {
      params.start_date = format(startOfMonth(new Date()), "yyyy-MM-dd");
      params.end_date = format(endOfMonth(new Date()), "yyyy-MM-dd");
    }
    return params;
  }, [timeRange, startDate, endDate]);

  const fetchMilkings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const data = await getMilkingsByAnimalIdPaginated(animalId, {
        ...params,
        page: page + 1, // backend is 1-based
        page_size: rowsPerPage,
      });
      const summaryData = await getAnimalMilkingSummary(animalId, params);
      const trendsData = await getMilkTrends({
        ...params,
        animal_id: animalId,
        aggregation,
      });
      setMilkings(data.items || []);
      setTotal(data.total || 0);
      setSummary(summaryData);
      setTrendPoints(trendsData?.points || []);
    } catch (err) {
      setError(err?.message || "Failed to fetch milking records");
    } finally {
      setLoading(false);
    }
  }, [animalId, page, rowsPerPage, buildParams, aggregation]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (newPageSize) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  useEffect(() => {
    fetchMilkings();
  }, [fetchMilkings]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const aggregatedData = useMemo(() => {
    return trendPoints.map((point) => ({
      key: point.period,
      total: point.total_production,
      estimated_income: point.estimated_income,
      morning: 0,
      evening: 0,
    }));
  }, [trendPoints]);

  useEffect(() => {
    if (aggregatedData.length > 60 && chartType === "bar") {
      setChartType("line");
      setAggregation((agg) => (agg === "daily" ? "weekly" : agg));
    }
  }, [aggregatedData.length, chartType]);

  // Prepare data for chart
  // const chartData = milkings
  //   .map((milking) => ({
  //     date: format(new Date(milking.date), "MMM dd"),
  //     morning: milking.morning_production,
  //     evening: milking.evening_production,
  //     total: milking.total_production,
  //   }))
  //   .reverse();

  const totalProduction = milkings.reduce(
    (sum, milking) => sum + milking.total_production,
    0
  );
  const averageDailyProduction =
    milkings.length > 0 ? (totalProduction / milkings.length).toFixed(2) : 0;
  const formatValue = (value) =>
    new Intl.NumberFormat("en-UG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  const formatCurrency = (value) => `UGX ${formatValue(value)}`;
  const summaryCards = [
    {
      label: "Total Records",
      value: summary?.total_records ?? total,
      tone: "primary",
    },
    {
      label: "Total Production",
      value: `${formatValue(summary?.total_production ?? totalProduction)} L`,
      tone: "success",
    },
    {
      label: "Avg Daily Production",
      value: `${formatValue(summary?.average_daily_production ?? averageDailyProduction)} L`,
      tone: "success",
    },
    {
      label: "Estimated Income",
      value: formatCurrency(summary?.estimated_income),
      tone: "primary",
    },
    {
      label: "Avg Price / Liter",
      value:
        summary?.average_price_per_liter == null
          ? "Not set"
          : formatCurrency(summary?.average_price_per_liter),
      tone: "primary",
    },
  ];

  const milkingColumns = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        minWidth: 150,
        flex: 1,
        valueGetter: (_, row) =>
          row?.date ? format(new Date(row.date), "MMM dd, yyyy") : "—",
      },
      {
        field: "morning_production",
        headerName: "Morning (L)",
        type: "number",
        minWidth: 130,
        flex: 1,
      },
      {
        field: "evening_production",
        headerName: "Evening (L)",
        type: "number",
        minWidth: 130,
        flex: 1,
      },
      {
        field: "total_production",
        headerName: "Total (L)",
        type: "number",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 120,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => {
                setSelected(params.row);
                setViewOpen(true);
              }}
              color="primary"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setSelected(params.row);
                setEditOpen(true);
              }}
              color="secondary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">Milking History</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate("/milking")}
            >
              Milk price settings
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Add New Record
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={handleTimeRangeChange}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {timeRange === "custom" && (
              <>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mb: 2 }}>
          Milk prices are configured from the Admin dashboard under{" "}
          <strong>Milk dashboard → Daily milk pricing</strong>.
        </Alert>

        <Box
          sx={{
            mb: 3,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          {summaryCards.map((card) => (
            <Paper
              key={card.label}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                  card.tone === "success"
                    ? "rgba(46, 125, 50, 0.22)"
                    : card.tone === "warning"
                    ? "rgba(237, 108, 2, 0.28)"
                    : "rgba(21, 101, 192, 0.2)",
                borderLeft: "5px solid",
                borderLeftColor:
                  card.tone === "success"
                    ? "success.main"
                    : card.tone === "warning"
                    ? "warning.main"
                    : "primary.main",
                boxShadow:
                  card.tone === "success"
                    ? "0 6px 24px rgba(46, 125, 50, 0.07)"
                    : card.tone === "warning"
                    ? "0 6px 24px rgba(237, 108, 2, 0.08)"
                    : "0 6px 24px rgba(21, 101, 192, 0.08)",
              }}
            >
              <Typography variant="overline" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {card.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* table / chat view tabs  */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Table View" />
          <Tab label="Chart View" />
        </Tabs>

        {/* export to csv button  */}
        <Grid container justifyContent="flex-end" sx={{ mb: 1 }}>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                exportMilkingsCsv(animalId, {
                  ...(timeRange === "custom" && {
                    start_date: format(startDate, "yyyy-MM-dd"),
                    end_date: format(endDate, "yyyy-MM-dd"),
                  }),
                  ...(timeRange === "thisMonth" && {
                    start_date: format(startOfMonth(new Date()), "yyyy-MM-dd"),
                    end_date: format(endOfMonth(new Date()), "yyyy-MM-dd"),
                  }),
                  ...(timeRange === "lastMonth" && {
                    start_date: format(
                      startOfMonth(subMonths(new Date(), 1)),
                      "yyyy-MM-dd"
                    ),
                    end_date: format(
                      endOfMonth(subMonths(new Date(), 1)),
                      "yyyy-MM-dd"
                    ),
                  }),
                })
              }
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {activeTab === 0 && (
          isMobile ? (
            <Stack spacing={1.5}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <MobileListCardSkeleton key={i} />
                  ))
                : milkings.length === 0
                  ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        No milking records found for the selected period.
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                      >
                        Add First Record
                      </Button>
                    </Box>
                  )
                  : milkings.map((row) => (
                      <MilkingRecordCard
                        key={row.id}
                        row={row}
                        onView={(r) => {
                          setSelected(r);
                          setViewOpen(true);
                        }}
                        onEdit={(r) => {
                          setSelected(r);
                          setEditOpen(true);
                        }}
                      />
                    ))}
              {milkings.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, pt: 1 }}>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => handleChangePage(null, page - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    Page {page + 1} of {Math.max(1, Math.ceil(total / rowsPerPage))}
                  </Typography>
                  <Button
                    size="small"
                    disabled={(page + 1) * rowsPerPage >= total}
                    onClick={() => handleChangePage(null, page + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Stack>
          ) : (
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
          >
            <DataGrid
              rows={milkings}
              columns={milkingColumns}
              getRowId={(r) => r.id}
              autoHeight
              rowCount={total}
              paginationMode="server"
              paginationModel={{ page, pageSize: rowsPerPage }}
              onPaginationModelChange={(model) => {
                if (model.pageSize !== rowsPerPage) {
                  handleChangeRowsPerPage(model.pageSize);
                  return;
                }
                handleChangePage(null, model.page);
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 },
                  printOptions: { disableToolbarButton: true },
                },
              }}
              disableRowSelectionOnClick
              sx={{ border: "none" }}
            />
            {milkings.length === 0 && (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No milking records found for the selected period.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                >
                  Add First Record
                </Button>
              </Box>
            )}
          </Paper>
          )
        )}
        {activeTab === 1 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <FormControl size="small">
                <InputLabel>Chart</InputLabel>
                <Select
                  value={chartType}
                  label="Chart"
                  onChange={(e) => setChartType(e.target.value)}
                >
                  <MenuItem value="bar">Bar</MenuItem>
                  <MenuItem value="line">Line</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Aggregation</InputLabel>
                <Select
                  value={aggregation}
                  label="Aggregation"
                  onChange={(e) => setAggregation(e.target.value)}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Paper
              sx={{
                p: 2,
                height: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {milkings.length === 0 ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    No data to chart for this period.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                  >
                    Add Milking Record
                  </Button>
                </Box>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    {/* Bar or Line chart based on selection */}
                    {chartType === "bar" ? (
                      <BarChart
                        data={aggregatedData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={COLORS.grid}
                        />
                        <XAxis
                          dataKey="key"
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          label={{
                            value: "Liters",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="total"
                          name="Total"
                          fill={COLORS.total}
                        />
                        <Brush dataKey="key" height={24} />
                      </BarChart>
                    ) : (
                      //               <LineChart
                      //                 data={aggregatedData}
                      //                 margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      //               >
                      //                 <CartesianGrid strokeDasharray="3 3" />
                      //                 <XAxis
                      //                   dataKey="key"
                      //                   angle={-45}
                      //                   textAnchor="end"
                      //                   height={60}
                      //                 />
                      //                 <YAxis
                      //                   label={{
                      //                     value: "Liters",
                      //                     angle: -90,
                      //                     position: "insideLeft",
                      //                   }}
                      //                 />
                      //                 <Tooltip />
                      //                 <Legend />
                      //                 <Line
                      //                   type="monotone"
                      //                   dataKey="total"
                      //                   name="Total"
                      //                   dot={false}
                      //                 />
                      //                 {/* optional toggles for these */}
                      //                 {/* <Line type="monotone" dataKey="morning" name="Morning" dot={false} />
                      // <Line type="monotone" dataKey="evening" name="Evening" dot={false} /> */}
                      //                 <Brush dataKey="key" height={24} />
                      //               </LineChart>
                      <LineChart
                        data={aggregatedData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={COLORS.grid}
                        />
                        <XAxis
                          dataKey="key"
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          label={{
                            value: "Liters",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="total"
                          name="Total"
                          stroke={COLORS.total}
                          strokeWidth={2}
                          dot={false}
                        />
                        <Brush dataKey="key" height={24} />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </>
              )}
            </Paper>
          </>
        )}
      </Box>
      {/* View modal */}
      <MilkingViewModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        record={selected}
      />

      {/* Create modal */}
      <MilkingFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialValues={null}
        title="Add Milking Record"
        onSubmit={async (payload) => {
          await createMilking({ ...payload, animal_id: animalId });
          await fetchMilkings();
        }}
      />

      {/* Edit modal */}
      <MilkingFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initialValues={selected}
        title="Edit Milking Record"
        onSubmit={async (payload) => {
          // use selected.id to target record
          await updateMilking(selected.id, payload);
          await fetchMilkings();
        }}
      />
    </>
  );
};

export default AnimalMilking;
